import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Pagination,
  Skeleton,
  StatusBadge,
} from '@/components/ui';
import type { CouponDefinition } from '@/lib/api-admin';
import { useCouponIssuedQuery, type CouponIssuedStatus } from '../hooks/useCouponIssuedQuery';

const PAGE_SIZE = 20;

const formatDate = (iso: string | null): string => (iso ? iso.slice(0, 10) : '—');

// 사용 상태 badge 변형 — USED=사용됨(success), RESTORED=복원됨(warning), ISSUED=미사용(neutral).
const STATUS_VARIANT: Record<CouponIssuedStatus, 'neutral' | 'success' | 'warning'> = {
  ISSUED: 'neutral',
  USED: 'success',
  RESTORED: 'warning',
};
const STATUS_LABEL_KO: Record<CouponIssuedStatus, string> = {
  ISSUED: '미사용',
  USED: '사용됨',
  RESTORED: '복원됨',
};

interface Props {
  coupon: CouponDefinition;
  onClose: () => void;
}

/**
 * [coupon-audit] 쿠폰별 발급 내역 다이얼로그 — 종전 BO 에 발급 현황 조회 UI 가 없어
 * GET /{couponId}/issued 가 미소비였다. userId·할인·만료일·사용 상태·발급 경로를 페이지로
 * 노출한다. 사용 상태(status/orderId/redeemedAt)는 be-snapshot additive — USED 행은 사용
 * 주문 링크를 함께 노출하고, 미배포 BE(키 생략) 행은 '—' 로 graceful 표시한다.
 */
export const CouponIssuedListDialog = ({ coupon, onClose }: Props) => {
  const { t } = useTranslation('common');
  const [page, setPage] = useState(1);
  const query = useCouponIssuedQuery(coupon.id, page, PAGE_SIZE);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="coupon-issued-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('coupons.issuedTitle', '발급 내역')} — {coupon.title}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs" style={{ color: 'var(--content-muted)' }}>
          {t('coupons.issuedSummary', '총 발급 {{issued}}건 / 최대 {{max}}', {
            issued: coupon.totalIssued,
            max: coupon.maxQuantity ?? '∞',
          })}
        </p>

        {query.isPending ? (
          <div role="status" aria-live="polite" className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-[32px] w-full" />
            ))}
          </div>
        ) : query.isError ? (
          <div role="alert" className="py-6 text-center text-sm" style={{ color: 'var(--status-danger-fg)' }}>
            <p>{t('coupons.issuedLoadFailed', '발급 내역을 불러오지 못했습니다.')}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => query.refetch()}>
              {t('coupons.retry', '재시도')}
            </Button>
          </div>
        ) : query.data.items.length === 0 ? (
          <p className="py-8 text-center text-sm" style={{ color: 'var(--content-muted)' }}>
            {t('coupons.issuedEmpty', '발급된 쿠폰이 없습니다.')}
          </p>
        ) : (
          <div className="overflow-hidden rounded-lg" style={{ border: '1px solid var(--divider-strong)' }}>
            <table className="w-full text-sm" data-testid="coupon-issued-table">
              <thead>
                <tr style={{ background: 'var(--surface-1)', borderBottom: '1px solid var(--divider-strong)' }}>
                  <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                    {t('coupons.issuedCols.userId', '회원 ID')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                    {t('coupons.issuedCols.discount', '할인금액')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                    {t('coupons.issuedCols.expiresAt', '만료일')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                    {t('coupons.issuedCols.status', '사용 상태')}
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>
                    {t('coupons.issuedCols.channel', '발급 경로')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((row) => (
                  <tr key={row.id} style={{ borderBottom: '1px solid var(--divider-soft)' }} data-testid="coupon-issued-row">
                    <td className="px-3 py-2 font-mono text-xs" style={{ color: 'var(--content-default)' }}>
                      {row.userId}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--content-default)' }}>
                      {row.discountAmount.toLocaleString('ko-KR')}
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums" style={{ color: 'var(--content-muted)' }}>
                      {formatDate(row.expiresAt)}
                    </td>
                    <td className="px-3 py-2" data-testid="coupon-issued-status">
                      {row.status ? (
                        <span className="flex flex-wrap items-center gap-1.5">
                          <StatusBadge
                            label={t(`coupons.issuedStatus.${row.status}`, STATUS_LABEL_KO[row.status])}
                            variant={STATUS_VARIANT[row.status]}
                            size="sm"
                          />
                          {/* USED — 사용 주문 링크 + 사용 일자(BE: USED 일 때만 non-null). */}
                          {row.status === 'USED' && row.orderId ? (
                            <Link
                              to={`/orders/${row.orderId}`}
                              className="text-xs underline-offset-2 hover:underline"
                              style={{ color: 'var(--accent-emphasis)' }}
                              onClick={onClose}
                            >
                              {t('coupons.issuedUsedOrder', '사용 주문')}
                            </Link>
                          ) : null}
                          {row.status === 'USED' && row.redeemedAt ? (
                            <span className="text-[11px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
                              {formatDate(row.redeemedAt)}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        // 미배포 BE(status 키 생략) — graceful.
                        <span style={{ color: 'var(--content-muted)' }}>—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {/* downloadable=true 면 고객이 직접 다운로드(PDP 등), false 면 운영자 발급. */}
                      <StatusBadge
                        label={
                          row.downloadable
                            ? t('coupons.issuedChannel.download', '다운로드')
                            : t('coupons.issuedChannel.admin', '운영자 발급')
                        }
                        variant={row.downloadable ? 'info' : 'neutral'}
                        size="sm"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          total={query.data?.totalItems ?? 0}
          currentPage={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          className="mt-2"
        />
      </DialogContent>
    </Dialog>
  );
};
